import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import { useRouteLoaderData } from '@remix-run/react';

import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import remixI18n from '~/i18next.server';
import type { Community, CurrentCommunityMember } from '~/interfaces/community';
import type { OrganizationSubscription, SubscriberPreference } from '~/interfaces/subscriber_preferences';
import type { Subscription } from '~/interfaces/subscriptions';
import { getCommunityLoaderData } from '~/services/api/community/getCommunityLoaderData.server';
import getOrganizationSubscriptions from '~/services/api/subscription/getOrganizationSubscriptions.server';
import getPreferences from '~/services/api/subscription/getPreferences.server';
import getSubscriptionProfile from '~/services/api/subscription/getSubsciptionProfile.server';
import { loaderSession } from '~/session';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';
import { publicationFromMatches } from '~/utils/publicationFromLoaderData';

import Page from './_components/v2/Page';
import ReadOnlyPage from './_components/v2/ReadOnlyPage';
import { getSiteFontsData } from './_utils/withSiteFonts';

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const publication = publicationFromMatches(matches);
  const metaTitle = data?.metaTitle;

  if (!publication || !metaTitle) {
    return [{ robots: 'noindex' }];
  }

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(data?.requestUrl),
    matches,
    returnEarlyCheck: !metaTitle,
    overrides: {
      robots: 'noindex',
      title: metaTitle,
      description: `Preferences - ${publication.name}`,
    },
  });
};

export const loader: LoaderFunction = async ({ request, params, context }) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);
  const getTranslation = (publication: { language?: string } | null | undefined) => {
    const locale = publication?.language || 'en';
    return remixI18n.getFixedT(locale, 'common');
  };

  const sitePromise = context.getSite(request, session);
  const communityDataPromise = getCommunityLoaderData(request, session, sitePromise);
  const [site, subscription, preferences, organizationSubscriptions, [community, currentCommunityMember]] =
    await Promise.all([
      sitePromise,
      getSubscriptionProfile(request, session, params?.subscriberId),
      getPreferences(request, session, params?.subscriberId),
      getOrganizationSubscriptions(request, session, params?.subscriberId),
      communityDataPromise,
    ]);
  const publication = site.publication;

  if (!subscription) {
    const t = await getTranslation(publication);
    throw new Response(t('forms.toast.no_subscription'), { status: 404 });
  }

  const [t, { fontsCss: renderableFontsCss, adobeFontUrls }] = await Promise.all([
    getTranslation(publication),
    getSiteFontsData(site),
  ]);

  return json({
    requestUrl: url.href,
    subscription,
    preferences,
    organizationSubscriptions,
    community,
    currentCommunityMember,
    subscriberId: params?.subscriberId,
    metaTitle: t('meta.titles.preferences'),
    renderableFontsCss,
    adobeFontUrls,
  });
};

export interface PreferencesLoaderData {
  requestUrl: string;
  subscription: Subscription;
  preferences: SubscriberPreference[] | null;
  organizationSubscriptions: OrganizationSubscription[] | null;
  community: Community | null;
  currentCommunityMember: CurrentCommunityMember | null;
  subscriberId: string;
  isLoggedIn: boolean;
  metaTitle: string;
}

const PreferencesPage = () => {
  const loaderData = useTypedLoaderData<PreferencesLoaderData>();
  const {
    site,
    isLoggedIn,
    isFetchingSubscription,
    subscription: loggedInUserSubscription,
  } = useRouteLoaderData<typeof loader>('root');
  const publication = site?.publication;

  if (!site || !publication || !loaderData.subscription) {
    // show the refresh page message if we didn't get server data
    throw new Error('RefreshPageError');
  }

  if (isFetchingSubscription) {
    return null;
  }

  const isReadOnly = isLoggedIn && loaderData.subscriberId !== loggedInUserSubscription?.subscriber_id;
  if (isReadOnly) {
    return <ReadOnlyPage site={site} loggedInSubscriberId={loggedInUserSubscription?.subscriber_id || ''} />;
  }

  return (
    <Page
      site={site}
      publication={publication}
      subscriberId={loaderData.subscriberId}
      subscription={loaderData.subscription}
      isLoggedIn={isLoggedIn}
      community={loaderData.community}
      currentCommunityMember={loaderData.currentCommunityMember}
    />
  );
};

export default PreferencesPage;
